import { type PsdSearchResult, REVISION_STATUSES } from '@epc/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { FileText, Folder, Search as SearchIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { FileLink } from './shared';

/**
 * Поиск по ПСД (ПД-20): по шифру и наименованию комплекта и листа, по тексту листа — надписи, основная
 * надпись, спецификации. Строка поиска над реестром комплектов; найденное ведёт в карточку комплекта и на лист.
 */

export function PsdSearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  const box = useRef<HTMLInputElement>(null);
  // Адрес меняется (назад, ссылка) — строка следует за ним, но не пока человек печатает
  useEffect(() => {
    if (document.activeElement !== box.current) setText(value);
  }, [value]);
  // Запрос уходит, когда человек перестал печатать
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), 350);
    return () => clearTimeout(t);
  }, [text, value, onChange]);
  return (
    <div className="relative mb-4">
      <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3" />
      <Input
        ref={box}
        type="search"
        aria-label="Поиск по ПСД"
        className="h-11 pl-9 md:h-10"
        placeholder="Шифр, лист, слово из надписи или спецификации"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
}

export function PsdSearchResults({ q, projectId }: { q: string; projectId?: string }) {
  const r = useQuery({
    queryKey: ['psd-search', q, projectId ?? 'all'],
    queryFn: () => api<PsdSearchResult>('/psd/search', { query: { q, projectId } }),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
  if (q.trim().length < 2) return <p className="text-[14px] text-ink-2">Введите хотя бы два знака.</p>;
  if (r.isPending) return <Spinner />;
  if (r.isError) return <ErrorBox error={r.error} />;
  const { hits, pending, scans } = r.data;
  return (
    <div className="flex flex-col gap-3">
      {hits.length ? (
        <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface">
          {hits.map((h) => (
            <li
              key={`${h.kind}-${h.setId}-${h.sheetId}-${h.fileId}-${h.revisionId}`}
              className="flex flex-col gap-1 px-4 py-3 text-[14px]"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                {h.kind === 'set' ? (
                  <Folder className="size-4 shrink-0 text-ink-3" />
                ) : (
                  <FileText className="size-4 shrink-0 text-ink-3" />
                )}
                <Link
                  to="/psd/sets/$setId"
                  params={{ setId: h.setId }}
                  search={(h.revisionId ? { rev: h.revisionId } : {}) as never}
                  className="inline-flex min-h-11 items-center font-medium underline-offset-4 hover:underline md:min-h-0 [overflow-wrap:anywhere]"
                >
                  {h.setCode}
                </Link>
                {h.sheetId ? (
                  <Link
                    to="/psd/sheets/$sheetId"
                    params={{ sheetId: h.sheetId }}
                    className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                  >
                    лист {h.sheetNumber}
                  </Link>
                ) : null}
                {h.revision ? <span className="num text-ink-2">{h.revision}</span> : null}
                {h.revisionStatus ? (
                  <Badge tone={h.revisionStatus === 'production' ? 'success' : undefined}>
                    {REVISION_STATUSES[h.revisionStatus as keyof typeof REVISION_STATUSES] ??
                      h.revisionStatus}
                  </Badge>
                ) : null}
                <span className="text-[12px] text-ink-3">{h.projectCode}</span>
              </div>
              <div className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                {h.sheetName ?? h.setName}
              </div>
              {h.snippet ? (
                <p className="text-[13px] text-ink [overflow-wrap:anywhere]">
                  <Marked text={h.snippet} />
                </p>
              ) : null}
              {h.kind === 'text' && !h.sheetId && h.fileId ? (
                <div className="text-[13px]">
                  <FileLink id={h.fileId} name={h.fileName ?? 'файл'} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Ничего не найдено">
          Проверьте написание или ищите по шифру комплекта и номеру листа.
        </EmptyState>
      )}
      {pending || scans ? (
        <p className="text-[13px] text-ink-3">
          {pending ? `Текст ещё читается у ${pending} PDF — они найдутся через несколько минут. ` : ''}
          {scans ? `${scans} PDF — сканы без текстового слоя: по ним ищется только шифр и наименование.` : ''}
        </p>
      ) : null}
    </div>
  );
}

/** Найденное слово в «…» — выделяется */
function Marked({ text }: { text: string }) {
  const parts = text.split(/(«[^»]*»)/);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('«') && p.endsWith('»') ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: части одной строки, порядок не меняется
          <mark key={i} className="rounded-sm bg-warning-soft px-0.5 text-ink">
            {p.slice(1, -1)}
          </mark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: части одной строки, порядок не меняется
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}
